import * as React from 'react';
import {
  Controller,
  useWatch,
  type Control,
  type FieldValues,
  type Path,
  type UseFormRegister,
} from 'react-hook-form';

import { Combobox, type ComboboxOption } from '@/components/combobox';
import { Input } from '@/components/ui/input';

import { useReferenceCompanies } from '../api/use-reference-companies';
import { safeNumber } from './final-settlement-parts';
import { FieldShell } from './form-helpers';

// The specialised widgets the model-driven field renderer delegates to: the
// company combobox and the trade total with its qty × rate calculation hint.

type TradeCurrency = 'USD' | 'EUR';

export function TotalAmountField<T extends FieldValues>(props: {
  readonly name: Path<T>;
  readonly label: string;
  readonly register: UseFormRegister<T>;
  readonly control: Control<T>;
  readonly error: string | undefined;
  readonly required?: boolean;
  readonly disabled: boolean;
}): React.JSX.Element {
  const qty = safeNumber(useWatch({ control: props.control, name: 'amount_qt_mt' as Path<T> }));
  const unitPrice = safeNumber(
    useWatch({ control: props.control, name: 'market_reference_price' as Path<T> }),
  );
  const watchedCurrency = useWatch({ control: props.control, name: 'currency' as Path<T> });
  const currency: TradeCurrency = watchedCurrency === 'EUR' ? 'EUR' : 'USD';
  const showHint = qty > 0 && unitPrice > 0;
  return (
    <FieldShell id={props.name} label={props.label} error={props.error} required={props.required}>
      <Input
        id={props.name}
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        disabled={props.disabled}
        {...props.register(props.name, { valueAsNumber: true })}
      />
      {showHint && (
        <p className="text-muted-foreground text-xs" data-testid="trade-total-calc">
          Calculated: {formatQty(qty)} MT × {formatCurrency(unitPrice, currency)} ={' '}
          {formatCurrency(qty * unitPrice, currency)}
        </p>
      )}
    </FieldShell>
  );
}

export function CompanyField<T extends FieldValues>(props: {
  readonly name: Path<T>;
  readonly label: string;
  readonly control: Control<T>;
  readonly disabled: boolean;
  readonly required?: boolean;
  readonly error: string | undefined;
}): React.JSX.Element {
  const companiesQuery = useReferenceCompanies();
  const options: ComboboxOption[] = React.useMemo(
    () => (companiesQuery.data ?? []).map((c) => ({ value: c.name, label: c.name })),
    [companiesQuery.data],
  );
  return (
    <FieldShell id={props.name} label={props.label} error={props.error} required={props.required}>
      <Controller
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <Combobox
            id={props.name}
            options={options}
            value={typeof field.value === 'string' ? field.value : undefined}
            onChange={(next) => {
              field.onChange(next ?? '');
            }}
            loading={companiesQuery.isLoading}
            disabled={props.disabled}
            placeholder="Pick a company…"
            searchPlaceholder="Search companies…"
            emptyMessage="No companies found"
            clearable
            aria-invalid={props.error !== undefined}
          />
        )}
      />
    </FieldShell>
  );
}

function formatCurrency(amount: number, currency: TradeCurrency): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

function formatQty(qty: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(qty);
}
